// -----------------------------------------------------------------------------
// In-memory token store.
//
// Rationale: storing the JWT in localStorage/sessionStorage exposes it to any
// XSS leak (third-party script, dependency compromise). Holding the token in a
// module-scoped variable means a hard browser refresh forces re-login, which
// we accept as the v1 trade-off.
// -----------------------------------------------------------------------------

export interface StoredToken {
  /** Raw JWT string sent as `Authorization: Bearer ${accessToken}`. */
  accessToken: string;
  /** Epoch milliseconds (UTC) when the JWT `exp` claim says it expires. */
  expiresAtMs: number;
}

type Listener = (token: StoredToken | null) => void;

let current: StoredToken | null = null;
const listeners = new Set<Listener>();

export const TokenStore = {
  get(): StoredToken | null {
    return current;
  },

  set(token: StoredToken): void {
    current = token;
    listeners.forEach((listener) => listener(current));
  },

  clear(): void {
    current = null;
    listeners.forEach((listener) => listener(current));
  },

  /** True when a token is present AND not yet expired. */
  isValid(nowMs: number = Date.now()): boolean {
    return current !== null && current.expiresAtMs > nowMs;
  },

  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};
